/**
 * AdminLayout — coquille dédiée à la zone administrateur.
 * Badges live : erreurs non résolues (Analytics) + messages non lus (Contacts).
 */
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  ShieldAlert,
  LayoutDashboard,
  ScrollText,
  Users as UsersIcon,
  Activity,
  Mail,
  PenSquare,
  ArrowLeft,
  AlertTriangle,
  Ban,
  Shield,
  Settings as SettingsIcon,
  BarChart3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/components/admin/RoleGuard";
import { cn } from "@/lib/utils";
import { fetchAnalyticsKpis, fetchContactMessages } from "@/lib/admin-api";

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ errors: 0, unread_contacts: 0 });

  useEffect(() => {
    if (loading || !hasRole(user, "admin")) return;
    async function load() {
      try {
        const [kpis, contacts] = await Promise.all([
          fetchAnalyticsKpis().catch(() => null),
          fetchContactMessages({ page: 1, limit: 1 }).catch(() => null),
        ]);
        setCounts({
          errors: kpis?.unresolved_errors || 0,
          unread_contacts: contacts?.unread || 0,
        });
      } catch { /* silent */ }
    }
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [loading, user]);

  const nav = [
    { to: "/admin", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, testId: "admin-nav-dashboard" },
    { to: "/admin/security/audit", label: "Journal d'audit", icon: ScrollText, testId: "admin-nav-audit" },
    { to: "/admin/security/events", label: "Événements sécu.", icon: AlertTriangle, testId: "admin-nav-events" },
    { to: "/admin/security/sessions", label: "Sessions actives", icon: Shield, testId: "admin-nav-sessions" },
    { to: "/admin/security/ip-rules", label: "Règles IP", icon: Ban, testId: "admin-nav-ip" },
    { to: "/admin/settings", label: "Paramètres", icon: SettingsIcon, testId: "admin-nav-settings" },
    { to: "/admin/users", label: "Utilisateurs", icon: UsersIcon, testId: "admin-nav-users" },
    { to: "/admin/resources", label: "Ressources", icon: Activity, testId: "admin-nav-resources" },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "admin-nav-analytics", badge: counts.errors },
    { to: "/admin/contacts", label: "Contacts", icon: Mail, testId: "admin-nav-contacts", badge: counts.unread_contacts },
    { to: "/admin/content", label: "Contenu", icon: PenSquare, testId: "admin-nav-content" },
    { to: "/admin/config", label: "Configuration", icon: SettingsIcon, testId: "admin-nav-config" },
  ];

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Chargement…
      </div>
    );
  }
  if (!hasRole(user, "admin")) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-layout">
      <aside
        className="hidden w-64 shrink-0 border-r border-red-200/60 bg-red-50/30 p-4 dark:border-red-900/30 dark:bg-red-950/10 md:block"
        data-testid="admin-sidebar"
      >
        <div className="mb-6 flex items-center gap-2 px-2">
          <ShieldAlert className="h-5 w-5 text-red-700 dark:text-red-400" />
          <div>
            <div className="text-sm font-bold tracking-tight text-red-800 dark:text-red-300">
              ADMIN-VITAE™
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Console de gouvernance
            </div>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {nav.map(({ to, label, icon: Icon, end, testId, disabled, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition",
                  isActive
                    ? "bg-red-700 text-white shadow-sm"
                    : "text-foreground/80 hover:bg-red-100/60 hover:text-foreground dark:hover:bg-red-900/20",
                  disabled && "pointer-events-none opacity-40"
                )
              }
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{label}</span>
              {badge > 0 && (
                <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white" data-testid={`${testId}-badge`}>
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
              {disabled && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] uppercase">
                  Bientôt
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-6 border-t border-red-200/60 pt-4 dark:border-red-900/30">
          <button
            onClick={() => navigate("/app/dashboard")}
            data-testid="admin-back-to-app"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'application
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="flex items-center gap-2 border-b border-red-200/60 bg-red-700 px-4 py-2 text-xs font-medium text-white shadow-sm"
          data-testid="admin-banner"
        >
          <ShieldAlert className="h-4 w-4" />
          Zone d'administration — toutes vos actions sont journalisées (RGPD : audit log 180 j).
          <span className="ml-auto opacity-80">{user?.email}</span>
        </div>

        <main className="flex-1 overflow-auto px-4 py-6 lg:px-8">
          <Outlet />
        </main>

        <footer
          className="border-t border-red-200/40 bg-red-50/20 px-4 py-2 text-center text-[10px] text-muted-foreground dark:border-red-900/20 dark:bg-red-950/10"
          data-testid="admin-footer"
        >
          VITAE-PUBLICA ADMIN — ELY Mustapha — PMP I-PMP IBM Full Stack Developer
        </footer>
      </div>
    </div>
  );
}
